/**
 * Chart form drafts (Phase 18 M10): the text of an unsaved chart entry form, per visit and entry
 * type, so a lock, a reload or a tab switch never loses what the clinician typed.
 *
 * - With an unlocked offline store (enrolled device) drafts are AES-GCM encrypted in the
 *   `drafts` store, so they survive the idle lock and a reload, and a PIN shred deletes them.
 * - Otherwise (not enrolled, or locked) they are kept in memory only: never written to disk
 *   unencrypted. They last until a reload.
 * - `clearAll()` runs on sign-out (both copies).
 *
 * Drafts hold PHI: never log them.
 */
import { OfflineSession } from './offlineSession';

export interface ChartDraft<T = unknown> {
  visitId: string;
  entryType: string;
  values: T;
  savedAt: string;
}

const STORE = 'drafts' as const;
const keyOf = (visitId: string, entryType: string) => `${visitId}:${entryType}`;
const memory = new Map<string, ChartDraft>();

const store = () => {
  try {
    return OfflineSession.getStore();
  } catch {
    return null;
  }
};

export const ChartDrafts = {
  async get<T>(visitId: string, entryType: string): Promise<ChartDraft<T> | undefined> {
    const key = keyOf(visitId, entryType);
    const s = store();
    if (s) {
      try {
        const row = await s.get<ChartDraft<T>>(STORE, key);
        if (row) return row;
      } catch {
        // unreadable row: fall back to memory
      }
    }
    return memory.get(key) as ChartDraft<T> | undefined;
  },

  async put<T>(visitId: string, entryType: string, values: T, now: Date = new Date()): Promise<ChartDraft<T>> {
    const draft: ChartDraft<T> = { visitId, entryType, values, savedAt: now.toISOString() };
    const key = keyOf(visitId, entryType);
    const s = store();
    if (s) {
      await s.put(STORE, key, draft);
      memory.delete(key);
    } else {
      memory.set(key, draft);
    }
    return draft;
  },

  async remove(visitId: string, entryType: string): Promise<void> {
    const key = keyOf(visitId, entryType);
    memory.delete(key);
    const s = store();
    if (s) await s.delete(STORE, key).catch(() => undefined);
  },

  /** Every draft for a visit, newest first */
  async listForVisit(visitId: string): Promise<ChartDraft[]> {
    const found = new Map<string, ChartDraft>();
    const s = store();
    if (s) {
      try {
        for (const { key, value } of await s.getAll<ChartDraft>(STORE)) {
          if (value?.visitId === visitId) found.set(key, value);
        }
      } catch {
        // locked between calls: memory only
      }
    }
    memory.forEach((value, key) => { if (value.visitId === visitId && !found.has(key)) found.set(key, value); });
    return Array.from(found.values()).sort((a, b) => b.savedAt.localeCompare(a.savedAt));
  },

  /** Sign-out: forget every draft on this device for the signed-in user */
  async clearAll(): Promise<void> {
    memory.clear();
    const s = store();
    if (s) await s.clear(STORE).catch(() => undefined);
  },
};

export default ChartDrafts;
